import React from 'react';

import { Box, Button, Stack, TextField, Typography } from '@mui/material';

import ReadOnlyField, { type ReadOnlyFieldConfig } from '~/components/Offers/Detail/OfferBuilder/shared/ReadOnlyField';
import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { usePropertySetupContext } from '../../PricingToolSessionContext';

import type { PropertySetupValues } from './types';

function display(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value);
}

function buildReadOnlyFields(values: PropertySetupValues): Array<ReadOnlyFieldConfig> {
  return [
    { label: 'Property name', value: display(values.propertyName) },
    { label: 'Offer country', value: display(values.offerCountry) },
    { label: 'Sub-destination', value: display(values.subDestination) },
    { label: 'Guests per room', value: display(values.guestsPerRoom) },
    { label: 'Travel from', value: display(values.travelFrom), type: 'date' },
    { label: 'Travel to', value: display(values.travelTo), type: 'date' },
    { label: 'Currency', value: display(values.currency) },
  ];
}

export default function PropertySetupTab() {
  const { values, setFxRate, setPrvReqLeadPrice, autoResolveFxRate } = usePropertySetupContext();

  if (!values) {
    return (
      <TabPanelShell subtitle="Loading opportunity details…" title="Property Setup">
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </TabPanelShell>
    );
  }

  const fields = buildReadOnlyFields(values);

  return (
    <TabPanelShell
      subtitle="Pre-filled from the opportunity. Edit the FX rate manually or re-run Auto."
      title="Property Setup"
    >
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          }}
        >
          {fields.map((field) => (
            <ReadOnlyField key={field.label} field={field} />
          ))}
        </Box>

        <Stack alignItems="flex-end" direction="row" spacing={1} sx={{ maxWidth: 240 }}>
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="FX rate (AUD → currency)"
            type="number"
            value={values.fxRate ?? ''}
            onChange={(event) => {
              const next = event.target.value === '' ? null : Number(event.target.value);
              setFxRate(Number.isFinite(next) ? next : null);
            }}
          />
          <Button color="secondary" size="small" variant="outlined" onClick={autoResolveFxRate}>
            Auto
          </Button>
        </Stack>

        <Stack alignItems="flex-end" direction="row" spacing={1} sx={{ maxWidth: 240 }}>
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="PRV required lead price"
            type="number"
            value={values.prvReqLeadPrice ?? ''}
            onChange={(event) => {
              const next = event.target.value === '' ? null : Number(event.target.value);
              setPrvReqLeadPrice(Number.isFinite(next) ? next : null);
            }}
          />
        </Stack>
      </Stack>
    </TabPanelShell>
  );
}
